import { memo, useEffect, useMemo, type ReactNode } from "react"
import { Link } from "react-router"
import {
  ArrowUpRight,
  Bot,
  Clock4,
  MessageSquareWarning,
  ThumbsUp,
} from "lucide-react"
import { format, formatDistance, type Locale } from "date-fns"
import { enUS, ja, zhTW } from "date-fns/locale"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { ellipsis } from "@f2e/shared/utils"

import { History, Workspace } from "@/types"
import { useToast } from "@/hooks"
import { useDeleteHistoryApi, useUpdateHistoryApi } from "@/hooks/api/histories"
import { useStore } from "@/store"
import { getConversationPreview } from "@/lib/conversationContent"
import { CopilotHiddenField } from "@/components/Copilot"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import { getChannelSourceLabelKey } from "../../history-source-labels"
import { formatLatestActorName } from "../../latestActorName"
import { ActionButton } from "./ActionButton"

/**
 * Copilot-aware history list item.
 *
 * Keep the hidden summary aligned with the visible card so Copilot can identify
 * the selected history, its latest message, reply state, participant, source,
 * agent, activity time, and credits.
 */
const maxTitleLength = 54
const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)

const getHistoryCardCopilotSnapshotValue = (
  history: History,
  latestMessagePreview: string,
  latestActorName: string,
  selected: boolean,
  displayUserId?: string
) => {
  const fields = [
    `id=${history.id}`,
    `selected=${selected ? "true" : "false"}`,
    `title=${history.name || ""}`,
    `latestMessage=${latestMessagePreview}`,
    `needsReply=${history.needsReply ? "true" : "false"}`,
  ]

  if (history.agentName) fields.push(`agentName=${history.agentName}`)
  if (history.updatedAt) fields.push(`updatedAt=${history.updatedAt}`)
  if (displayUserId) fields.push(`user=${displayUserId}`)
  if (history.participantName)
    fields.push(`participant=${history.participantName}`)
  if (history.latestMessage) fields.push(`latestActor=${latestActorName}`)
  if (history.channel?.name) fields.push(`channel=${history.channel.name}`)
  if (history.channel?.type) fields.push(`channelType=${history.channel.type}`)
  if (history.sourceType) fields.push(`sourceType=${history.sourceType}`)
  fields.push(`credits=${formatCredits(history.totalCostCredits ?? 0)}`)

  return fields.join("\n")
}

export interface HistoryCardProps {
  history: History
  selected?: boolean
  showWorkspaceBadge?: boolean
  onSelect?: (history: History) => void
  onDelete?: (history: History) => void
  onUpdate?: (history: History) => void
}

const HistoryCard = ({
  history,
  selected = false,
  showWorkspaceBadge = true,
  onSelect,
  onDelete,
  onUpdate,
}: HistoryCardProps) => {
  const { alertToast, successToast } = useToast()
  const workspaces = useStore((state) => state.workspaces)
  const workspace =
    workspaces.find((item) => item.id === history.workspace?.id) ||
    history.workspace
  const {
    error: deleteHistoryError,
    data: deleteRespData,
    fetchFn: deleteHistoryFn,
  } = useDeleteHistoryApi(history.id)
  const {
    error: updateHistoryError,
    data: updateRespData,
    fetchFn: updateHistoryFn,
  } = useUpdateHistoryApi(history.id)
  const { t, i18n } = useTranslation()
  const dateFnsLocale = useMemo(() => {
    const localeMap: Record<string, Locale> = {
      en: enUS,
      "zh-TW": zhTW,
      ja,
    }
    return localeMap[i18n.resolvedLanguage || i18n.language] || enUS
  }, [i18n.language, i18n.resolvedLanguage])
  const displayUserId =
    history.externalUserId?.trim() || history.creator?.email?.trim()
  const participantName =
    history.participantName?.trim() ||
    history.externalUserId?.trim() ||
    (history.sourceType === "console" ? history.creator?.email?.trim() : "")
  const latestMessagePreview = getConversationPreview(
    history.latestMessage?.preview || history.snippet
  )
  const helpfulCount = history.feedbackConversationCounts?.sys_helpful ?? 0
  const improveCount = history.feedbackConversationCounts?.sys_improve ?? 0
  const isLiveTest = history.sourceType === "console"
  const isCopilot = history.sourceType === "copilot"
  const isWorkspaceApi = history.sourceType === "workspace_api_key"
  const primaryTitle = history.name || t("historyList.card.untitled")
  const activityAt = history.latestMessage?.createdAt || history.updatedAt
  const channelTypeKey = getChannelSourceLabelKey(history.channel?.type)
  const channelTypeLabel = channelTypeKey
    ? t(`historyList.sourceSelect.options.${channelTypeKey}`)
    : history.channel?.type
  const channelName = history.channel?.name?.trim()
  const sourceLabel = isLiveTest
    ? t("historyList.sourceSelect.liveTest")
    : isCopilot
      ? t("historyList.sourceSelect.copilot")
      : isWorkspaceApi
        ? t("historyList.sourceSelect.workspaceApi")
        : channelName || channelTypeLabel
  const shouldAppendChannelType = Boolean(
    channelName &&
    channelTypeLabel &&
    !channelName
      .toLocaleLowerCase()
      .includes(channelTypeLabel.toLocaleLowerCase())
  )
  const conversationContext = [
    participantName,
    sourceLabel,
    shouldAppendChannelType ? channelTypeLabel : undefined,
  ]
    .filter(Boolean)
    .join(" · ")
  const latestActorName = formatLatestActorName({
    actor: history.latestMessage?.actor,
    actorName: history.latestMessage?.actorName,
    participantName,
    agentName: history.agentName,
    getFallbackName: (actor) => t(`historyList.card.latestActor.${actor}`),
  })

  useEffect(() => {
    if (!deleteHistoryError) return
    console.error("Failed to delete history", deleteHistoryError)

    alertToast({
      title: t("historyList.message.deleteFailed"),
      description: getUserFacingApiErrorMessage(
        deleteHistoryError,
        t,
        deleteRespData?.message || t("historyList.message.deleteFailed")
      ),
    })
  }, [deleteHistoryError, deleteRespData, alertToast, t])

  useEffect(() => {
    if (!updateHistoryError) return
    console.error("Failed to update history", updateHistoryError)

    alertToast({
      title: t("historyList.message.updateFailed"),
      description: getUserFacingApiErrorMessage(
        updateHistoryError,
        t,
        updateRespData?.message || t("historyList.message.updateFailed")
      ),
    })
  }, [updateHistoryError, updateRespData, alertToast, t])

  const handleDelete = () => {
    deleteHistoryFn({
      callbackFn: (resp) => {
        if (!resp) return

        successToast({
          title: t("historyList.message.deleted"),
          description: t("historyList.message.deletedDesc", {
            name: history.name?.slice(0, maxTitleLength) + "...",
          }),
        })
        onDelete?.(history)
      },
    })
  }

  const handleEdit = (updatedHistory: History) => {
    updateHistoryFn({
      jsonBody: { name: updatedHistory.name },
      callbackFn: (resp) => {
        if (!resp || !resp.data) return
        onUpdate?.(updatedHistory)
      },
    })
  }

  const historyHref = `/workspaces/${history.workspace.id}/histories/${history.id}`
  const isSelectable = typeof onSelect === "function"

  const handleSelect = () => onSelect?.(history)
  const renderRow = (
    content: ReactNode,
    baseClassName: string,
    selectableTrigger = false
  ) =>
    isSelectable ? (
      selectableTrigger ? (
        <button
          type="button"
          className={cn(
            "w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            baseClassName
          )}
          onClick={(event) => {
            event.stopPropagation()
            handleSelect()
          }}
        >
          {content}
        </button>
      ) : (
        <div className={cn("text-left", baseClassName)}>{content}</div>
      )
    ) : (
      <Link to={historyHref} className={cn("cursor-pointer", baseClassName)}>
        {content}
      </Link>
    )

  return (
    <Card
      className={cn(
        "relative gap-2 rounded-none border-0 py-0 transition-colors duration-150",
        isSelectable &&
          "cursor-pointer hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
        selected && "bg-muted/70 ring-1 ring-inset ring-border"
      )}
      onClick={isSelectable ? handleSelect : undefined}
      data-selected={selected ? "true" : "false"}
    >
      <CopilotHiddenField
        fieldKey={`historyList.item.${history.id}.summary`}
        value={getHistoryCardCopilotSnapshotValue(
          history,
          latestMessagePreview,
          latestActorName,
          selected,
          displayUserId
        )}
      />

      <CardHeader className="grid gap-1 px-4 pt-3 pb-0">
        <div
          className="flex flex-wrap items-start gap-x-2 gap-y-1"
          data-testid="history-card-primary-row"
        >
          <CardTitle className="min-w-40 flex-1 pt-0.5">
            {renderRow(
              primaryTitle,
              "line-clamp-1 text-base font-semibold leading-6 text-foreground transition-colors duration-150",
              true
            )}
          </CardTitle>

          <div className="-my-1 -mr-1 ml-auto flex shrink-0 items-center gap-1">
            {history.needsReply && (
              <Badge className="max-w-52 whitespace-nowrap border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <Clock4 className="mr-1 h-3 w-3" />
                {t("historyList.card.waitingForHuman")}
                {history.waitingSince && (
                  <span className="ml-1 font-normal">
                    ·{" "}
                    {t("historyList.card.waiting", {
                      duration: formatDistance(
                        new Date(history.waitingSince),
                        new Date(),
                        { locale: dateFnsLocale }
                      ),
                    })}
                  </span>
                )}
              </Badge>
            )}
            {!history.needsReply && activityAt && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default items-center gap-1 text-xs tabular-nums text-muted-foreground">
                    {formatDistance(new Date(activityAt), new Date(), {
                      locale: dateFnsLocale,
                    })}{" "}
                    {t("history.proAccordion.time.ago")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {format(new Date(activityAt), "yyyy-MM-dd HH:mm:ss")}
                </TooltipContent>
              </Tooltip>
            )}
            {isSelectable && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <Link
                      to={historyHref}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={t("historyList.detail.openFullPage")}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("historyList.detail.openFullPage")}
                </TooltipContent>
              </Tooltip>
            )}
            <ActionButton
              history={history}
              workspace={workspace as Workspace}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>
        </div>
        {conversationContext && (
          <div className="truncate text-xs font-medium text-muted-foreground">
            {conversationContext}
          </div>
        )}
      </CardHeader>

      <CardContent className="px-4 py-0">
        {latestMessagePreview ? (
          <div data-testid="history-latest-message">
            {renderRow(
              <>
                <span className="font-semibold text-foreground/80">
                  {latestActorName}
                  {t("historyList.card.speakerSeparator")}
                </span>{" "}
                <span>{latestMessagePreview}</span>
              </>,
              "line-clamp-2 break-words text-sm leading-5 text-muted-foreground transition-colors duration-150"
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("historyList.card.noMessages")}
          </span>
        )}
      </CardContent>

      <CardFooter className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-4 pt-0 pb-3 text-xs text-muted-foreground">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {history.agentName && (
            <Badge
              variant="outline"
              data-testid="history-agent-badge"
              className="max-w-[min(100%,14rem)] border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300"
            >
              <Bot className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {ellipsis(history.agentName, 20)}
              </span>
            </Badge>
          )}
          {showWorkspaceBadge && (
            <Badge variant="outline">
              {ellipsis(history.workspace.name || "", 20)}
            </Badge>
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2.5">
          {helpfulCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  data-testid="history-feedback-helpful-badge"
                  className="inline-flex cursor-default items-center gap-1 font-medium text-green-700 dark:text-green-500"
                >
                  <ThumbsUp className="h-3 w-3" />
                  {helpfulCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("historyList.feedbackBadge.helpfulTooltip")}
              </TooltipContent>
            </Tooltip>
          )}
          {improveCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  data-testid="history-feedback-improve-badge"
                  className="inline-flex cursor-default items-center gap-1 font-medium text-red-700 dark:text-red-500"
                >
                  <MessageSquareWarning className="h-3 w-3" />
                  {improveCount}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("historyList.feedbackBadge.improveTooltip")}
              </TooltipContent>
            </Tooltip>
          )}
          <span
            data-testid="history-total-credits-badge"
            className="shrink-0 font-medium tabular-nums text-foreground/65"
          >
            {t("historyList.card.credits", {
              credits: formatCredits(history.totalCostCredits ?? 0),
            })}
          </span>
        </div>
      </CardFooter>
    </Card>
  )
}

export default memo(HistoryCard)
